'use client';

import {
  Card,
  Flex,
  Heading,
  Text,
  Icon,
  Tooltip,
  Separator,
} from '@/_components';
import { useContext, useMemo, useState, useEffect, type FC } from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import { UserPreviewContext } from '@/_contexts/admin/UserPreviewProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { useRouter } from 'next/navigation';
import type { Questionnaire } from '@/_types/models/questionnaire';
import type { TaxReturn } from '@/_types/models/taxReturns';
import type { Company } from '@/_types/models/company';
import { getExpiredDaysAgo } from '@/_utils/dates';
import { getClientQuestionnaires } from '@/_api/client-questionnaires';
import { getTaxReturns } from '@/_api/client-tax-returns';
import styles from './MyTasks.module.css';
import type { MyTasksProps, TaskItem } from './MyTasks.types';

export const MyTasks: FC<MyTasksProps> = ({ isPreview = false }) => {
  const { companies, token } = useContext(UserContext);
  const { previewUserCompanies } = useContext(UserPreviewContext);
  const { preferences } = useContext(FirmSettingsContext);
  const { push } = useRouter();

  const [allEntitiesQuestionnaires, setAllEntitiesQuestionnaires] = useState<
    Array<Questionnaire>
  >([]);
  const [allEntitiesTaxReturns, setAllEntitiesTaxReturns] = useState<
    Array<TaxReturn>
  >([]);
  const [isLoadingAllData, setIsLoadingAllData] = useState<boolean>(false);

  const getTaskPriority = (
    task: Questionnaire | TaxReturn,
    type: 'questionnaire' | 'tax_return',
  ): 'urgent' | 'caution' | 'normal' => {
    if (type === 'questionnaire') {
      const questionnaire = task as Questionnaire;
      const dayDifference = getExpiredDaysAgo(questionnaire.due_date);
      const dayDifferenceCount = Math.abs(
        getExpiredDaysAgo(questionnaire.due_date),
      );

      if (dayDifference > 0 || dayDifferenceCount <= 1) {
        return 'urgent';
      }
      if (dayDifferenceCount <= 7) {
        return 'caution';
      }
      return 'normal';
    } else {
      // Tax returns always have normal priority
      return 'normal';
    }
  };

  const dashboardCompanies: Array<Company> = useMemo(
    () => (isPreview ? previewUserCompanies : companies || []),
    [isPreview, previewUserCompanies, companies],
  );

  // Fetch data for all companies
  useEffect(() => {
    if (!token || !dashboardCompanies?.length) return;

    const fetchAllEntitiesData = async () => {
      setIsLoadingAllData(true);
      try {
        // Fetch questionnaires for all companies
        const questionnairePromises = dashboardCompanies.map((company) =>
          getClientQuestionnaires({ companyId: company.id, token }),
        );
        const questionnaireResults = await Promise.all(questionnairePromises);
        const allQuestionnaires = questionnaireResults.flat();

        // Fetch tax returns for all companies
        const taxReturnPromises = dashboardCompanies.map((company) =>
          getTaxReturns({ companyId: company.id, token }),
        );
        const taxReturnResults = await Promise.all(taxReturnPromises);
        const allTaxReturns = taxReturnResults.flat();

        setAllEntitiesQuestionnaires(allQuestionnaires);
        setAllEntitiesTaxReturns(allTaxReturns);
      } catch (error) {
        console.error('Error fetching all entities data:', error);
      } finally {
        setIsLoadingAllData(false);
      }
    };

    fetchAllEntitiesData();
  }, [token, dashboardCompanies]);

  const tasksByEntity = useMemo(() => {
    const entityTasks: Record<string, TaskItem[]> = {};

    // Initialize with all available entities
    dashboardCompanies.forEach((company) => {
      entityTasks[company.name] = [];
    });

    // Process questionnaires
    allEntitiesQuestionnaires.forEach((questionnaire) => {
      const isCompleted = questionnaire.completed;
      if (isCompleted) return;

      const entityName = questionnaire.company_id
        ? dashboardCompanies.find((c) => c.id === questionnaire.company_id)
            ?.name
        : null;
      if (!entityName) return; // Skip if no company name

      const year = questionnaire.tax_year?.toString() || 'Unknown Year';

      if (!entityTasks[entityName]) {
        entityTasks[entityName] = [];
      }

      entityTasks[entityName].push({
        id: `q-${questionnaire.id}`,
        title: questionnaire.formatted_name || questionnaire.name,
        entityName,
        year,
        type: 'questionnaire',
        status: questionnaire.in_progress ? 'in_progress' : 'opened',
        priority: getTaskPriority(questionnaire, 'questionnaire'),
      });
    });

    // Process tax returns if new tax return delivery is enabled
    if (preferences?.new_tax_return_delivery_enabled) {
      allEntitiesTaxReturns.forEach((taxReturn) => {
        const isCompleted = taxReturn.all_signed && taxReturn.all_paid;

        if (isCompleted) return;

        const entityName = taxReturn.company_id
          ? dashboardCompanies.find((c) => c.id === taxReturn.company_id)?.name
          : null;
        if (!entityName) return; // Skip if no company name

        const year = taxReturn.tax_year?.toString() || 'Unknown Year';

        if (!entityTasks[entityName]) {
          entityTasks[entityName] = [];
        }

        // Tax returns always have in_progress status
        const taskStatus = 'in_progress';

        entityTasks[entityName].push({
          id: `tr-${taxReturn.id}`,
          title: taxReturn.tax_return_name || 'Tax Return',
          entityName,
          year,
          type: 'tax_return',
          status: taskStatus,
          priority: getTaskPriority(taxReturn, 'tax_return'),
        });
      });
    }

    const entitiesWithTasks: Record<string, TaskItem[]> = {};
    Object.entries(entityTasks).forEach(([entityName, tasks]) => {
      if (tasks.length > 0) {
        // Sort tasks by priority: urgent first, then caution, then normal
        const sortedTasks = tasks.sort((a, b) => {
          const priorityOrder = { urgent: 0, caution: 1, normal: 2 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        });
        entitiesWithTasks[entityName] = sortedTasks;
      }
    });

    return entitiesWithTasks;
  }, [
    dashboardCompanies,
    allEntitiesQuestionnaires,
    allEntitiesTaxReturns,
    preferences?.new_tax_return_delivery_enabled,
  ]);

  const totalTasks = useMemo(() => {
    return Object.values(tasksByEntity).reduce(
      (total, tasks) => total + tasks.length,
      0,
    );
  }, [tasksByEntity]);

  const onTaskClick = (task: TaskItem) => {
    if (task.type === 'questionnaire') {
      const questionnaireId = task.id.replace('q-', '');

      // Find the company hash for this entity
      const company = dashboardCompanies.find(
        (c) => c.name === task.entityName,
      );
      const companyHash = company?.hash_id;

      if (companyHash) {
        push(
          `/questionnaire/${companyHash}/?questionnaireId=${questionnaireId}`,
        );
      }
    } else if (task.type === 'tax_return') {
      const taxReturnId = task.id.replace('tr-', '');

      // Find the company ID for this entity
      const company = dashboardCompanies.find(
        (c) => c.name === task.entityName,
      );
      const companyId = company?.id;

      if (companyId) {
        push(`/tax-return-delivery/${taxReturnId}?companyId=${companyId}`);
      }
    }
  };

  return (
    <Card className={styles.myTasks}>
      <Flex isColumn={true} gap={4}>
        <Flex
          isBetween={true}
          isYCentered={true}
          className={styles.myTasksHeader}
        >
          <Flex isYCentered={true} gap={4}>
            <Icon name="list-check" size={16} />
            <Heading level={5}>My tasks</Heading>
            {totalTasks > 0 && (
              <Flex className={styles.taskCount}>
                <Text type="small" color="primary-on-color">
                  {totalTasks}
                </Text>
              </Flex>
            )}
          </Flex>
          <Tooltip placement="top">
            <Tooltip.Trigger>
              <Icon name="info" size={20} color="accent" />
            </Tooltip.Trigger>
            <Tooltip.Content>
              <Text type="small" color="currentcolor">
                This panel shows all active tasks across your entities and tax
                years: Questionnaires and Tax Returns. Items with no activity
                for 6 months are automatically archived, but you will still have
                access to them.
              </Text>
            </Tooltip.Content>
          </Tooltip>
        </Flex>

        {isLoadingAllData ? (
          <Flex isYCentered={true} className={styles.tasksContainer}>
            <Text type="small" color="secondary">
              Loading tasks...
            </Text>
          </Flex>
        ) : totalTasks === 0 ? (
          <Flex isYCentered={true} className={styles.tasksContainer}>
            <Text type="small" color="secondary">
              All tasks completed
            </Text>
          </Flex>
        ) : (
          <Flex isColumn={true} gap={8} className={styles.tasksContainer}>
            {Object.entries(tasksByEntity).map(([entityName, tasks], index) => {
              // Find the company to get entity type
              const company = dashboardCompanies.find(
                (c) => c.name === entityName,
              );
              const isIndividual = company?.entity_type === 'Individual';

              return (
                <Flex key={entityName} isColumn={true} gap={4}>
                  {index > 0 && <Separator />}
                  <Flex isYCentered={true} gap={2}>
                    <Icon
                      name={isIndividual ? 'user' : 'briefcase'}
                      size={16}
                      color="accent"
                    />
                    <Text type="small" color="accent">
                      {entityName}
                    </Text>
                  </Flex>
                  {Object.entries(
                    tasks.reduce(
                      (acc, task) => {
                        if (!acc[task.year]) acc[task.year] = [];
                        acc[task.year].push(task);
                        return acc;
                      },
                      {} as Record<string, TaskItem[]>,
                    ),
                  ).map(([year, yearTasks]) => (
                    <Flex key={year} isColumn={true} gap={2}>
                      <Text type="small" color="disabled" fontWeight="bold">
                        {year}
                      </Text>
                      <Flex isColumn={true} gap={2} isFullWidth={true}>
                        {yearTasks.map((task) => (
                          <Text
                            key={task.id}
                            type="small"
                            underline={true}
                            color="primary"
                            className={styles.taskItem}
                            onClick={() => onTaskClick(task)}
                          >
                            {task.title}
                          </Text>
                        ))}
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              );
            })}
          </Flex>
        )}
      </Flex>
    </Card>
  );
};
